import { useState, useEffect, useCallback } from 'react';
import { useAuth0, URLS } from '@livingsecurity/shared';

/**
 * Hook for handling step-up authentication
 *
 * Provides functionality to:
 * - Initiate step-up authentication flow
 * - Handle loading states
 * - Manage errors
 * - Reset loading state after redirect
 *
 * This hook uses Auth0 SDK's loginWithRedirect method to handle the authentication flow.
 * The state parameter is passed as appState to the SDK, which handles encoding internally.
 * The Auth0 action will decode this state to detect step-up authentication requests.
 */
export const useStepUpAuth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Properly consume the Auth0Context through useAuth0 hook
  const {
    loginWithRedirect,
    isAuthenticated,
    cancelImpersonate
  } = useAuth0();

  // Reset loading state when user returns from authentication
  useEffect(() => {
    if (isAuthenticated && isLoading) {
      setIsLoading(false);
    }
  }, [isAuthenticated, isLoading]);

  /**
   * Initiates the step-up authentication flow
   *
   * @param {string} returnTo Optional URL to redirect to after successful authentication
   */
  const initiateStepUp = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await loginWithRedirect({
        appState: { targetUrl: URLS.enterStepUp },
        stepUp: true,
      });

    } catch (err) {
      setError(err?.message || 'Unknown error occurred');
      console.error('Step-up authentication error:', err);
      setIsLoading(false);
      throw err; // Rethrow the error for the caller to handle
    }
  };

  const exitStepUp = () => {
    cancelImpersonate();
    localStorage.removeItem('AUTH_EXP');
    localStorage.removeItem('lsCustomLogo');
    sessionStorage.clear();

    window.lsLoginWithRedirect({
      appState: { targetUrl: URLS.exitStepUp },
      stepUp: false
    });
  };

  return {
    initiateStepUp,
    exitStepUp,
    isLoading,
    error,
  };
};
